/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import type { ComponentType } from "react";
import {
  InternalButton,
  type Shape,
  type Size,
  type Variant,
  type View,
} from "./internal-button";

export const Button = ({
  text,
  subText,
  icon: Icon,
  ariaLabel,
  variant,
  view = "default",
  shape = "squircle",
  size = "m",
  type = "button",
  disabled = false,
  progress = false,
  tooltip,
  tooltipOptions,
  onPress,
}: {
  /**
   * Visible text.
   * Also gives the button an accessible name.
   *
   * Should describe the action performed by the button.
   *
   * That is:
   * - either describe the action directly with a verb
   *   (e.g. "Submit", "Delete");
   * - or refer to an action mentioned above (e.g. "OK", "Confirm").
   *
   * Avoid using ambiguous texts such as "Yes/No" which may be interpreted
   * differently by people depending on their perception or cultural context.
   */
  text?: string;

  /**
   * Additional visible text.
   *
   * Also reads as a continuation of the main text
   * in the accessible name.
   *
   * For example, `<Button text="Send" subText="5 messages" />`
   * reads as "Send 5 messages".
   */
  subText?: string;

  /**
   * Icon component to be displayed in addition to visible text.
   */
  icon?: ComponentType<{ className?: string }>;

  /**
   * Gives the button an accessible name.
   * Allows to rename the accessible name for the button. Make different from text and subtext.
   * It must be used when the `icon` is set, but the `text` is not set.
   * The requirements for the text in `ariaLabel` are similar to  prop `text`.
   */
  ariaLabel?: string;

  /**
   * Visual variant of buttons.
   * @defaultValue "default"
   */
  variant?: Variant;

  /**
   * @deprecated use `variant` instead.
   *
   * Visual variant of buttons.
   * @defaultValue "default"
   */
  view?: View;

  /**
   * The shape of the button.
   * @defaultValue "squircle"
   */
  shape?: Shape;

  /**
   * The size of the button.
   * @defaultValue "m"
   */
  size?: Size;

  /**
   * Defines the type of button that sets its behavior in the form.
   * In appearance, the buttons of different types do not differ in any way,
   * but each such button has its own functions.
   * `button` - default button
   * `submit` - button for sending form data
   *
   * @defaultValue "button"
   */
  type?: "button" | "submit";

  /**
   * 	If `true`, the component is disabled.
   * @defaultValue false
   */
  disabled?: boolean;

  /**
   * If `true`, then the button displays the running process as a spinner.
   * If string, then the button displays progress text and running process as a spinner.
   * The progress text does not expand the container. If the text does not fit, then a colon is displayed.
   *
   * @defaultValue false
   */
  progress?: boolean | string;

  /**
   * Callback fired when mouse, touch or keyboard event occur on the button.
   */
  onPress?: () => void;

  /**
   * Text of tooltip, that is displayed by hover and focus.
   */
  tooltip?: string;

  /**
   * Additional props for tooltip.
   */
  tooltipOptions?: {
    /**
     * Defines the style of the tooltip.
     * `default` - For general explanations.
     * `info` - For additional information about functionality.
     * @defaultValue "default"
     */
    variant?: "default" | "info";

    /**
     * The placement of the element with respect to its anchor element.
     * @defaultValue "bottom"
     */
    placement?: "top" | "bottom" | "start" | "end";
  };
}) => {
  return (
    <InternalButton
      text={text}
      subText={subText}
      ariaLabel={ariaLabel}
      disabled={disabled}
      icon={Icon}
      onPress={onPress}
      progress={progress}
      shape={shape}
      size={size}
      type={type}
      variant={variant || view}
      tooltip={tooltip}
      tooltipOptions={tooltipOptions}
    />
  );
};
